import Services from "../Services/Services";
import { orgImageTypes } from "./orgImageTypes";
import { securePostMessage, validateInput } from "./security";
import { getEmbeddedOrgId } from "./embedOrg";

const isDev = process.env.NODE_ENV === "development";

// Debug logging utility - only logs in development
export const debugLog = (...args) => {
  if (isDev) {
    console.log(...args);
  }
};

// Strip [PAGE_CONTEXT]...[/PAGE_CONTEXT] tags from message content
export const stripPageContext = (text) => {
  if (!text || typeof text !== "string") return text;
  return text.replace(/\[PAGE_CONTEXT\][\s\S]*?\[\/PAGE_CONTEXT\]\s*/g, "").trim();
};

// The agent signals actions to the client with bracketed upper-case markers —
// [TRANSFER_TO_HUMAN], [ADDTOCART], [CASE NOTES LOCKED] and others it coins on
// its own. They drive behaviour and must never reach the customer as text, so
// this matches the shape rather than a fixed list, which the agent outgrows.
// Prose never contains a bracketed all-caps token, so this is safe to strip.
const CONTROL_MARKER_PATTERN = /\[[A-Z][A-Z0-9_ ]*\]/g;

// Remove control markers from text that is about to be shown or spoken.
// Detection of a specific marker must run against the raw content, before this.
export const stripControlMarkers = (text) => {
  if (!text || typeof text !== "string") return text;
  return text.replace(CONTROL_MARKER_PATTERN, "").replace(/\s{2,}/g, " ").trim();
};

// Shared AudioContext for TTS — once unlocked by a user gesture, stays unlocked
let sharedAudioCtx = null;
export async function getAudioContext() {
  if (!sharedAudioCtx) {
    sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (sharedAudioCtx.state === "suspended") {
    await sharedAudioCtx.resume();
  }
  return sharedAudioCtx;
}

// Safari lets a page speak only if speaking first started inside a tap. The
// reply is spoken seconds later, after the answer arrives, which Safari
// refuses without a word: the transcript appears and nothing is heard (owner,
// desktop Safari, 4 Oct). A silent utterance started in the tap lets the later
// one through. The card's voice does the same (sdk card-voice.js unlock).
export function unlockSpeech() {
  try {
    if (!window.speechSynthesis || typeof SpeechSynthesisUtterance === "undefined") return false;
    const silent = new SpeechSynthesisUtterance("");
    silent.volume = 0;
    window.speechSynthesis.speak(silent);
    return true;
  } catch (e) {
    return false;
  }
}

// Clean message text for TTS playback — strips HTML, URLs, markers
export const cleanTextForTTS = (text) => {
  if (!text || typeof text !== "string") return "";
  return stripControlMarkers(stripPageContext(text))
    .replace(/<[^>]*>/g, "")
    .replace(/https?:\/\/[^\s]+/g, "")
    // Strip emojis so TTS doesn't read "smiley face", "winking face" etc.
    .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{200D}\u{2B50}\u{2B55}\u{203C}-\u{3299}\u{1F3FB}-\u{1F3FF}]/gu, "")
    .replace(/\s{2,}/g, " ")
    .trim();
};

const baseUrl = process.env.REACT_APP_BACKEND_BASE_URL
  ? process.env.REACT_APP_BACKEND_BASE_URL
  : "https://api-yeshtery.dev.meetusvr.com/v1";

const full360Url =
  process.env.REACT_APP_360_BASE_URL ||
  process.env.REACT_APP_FILES_BASE_URL + "/shop360/pannellum/v3/index.html" ||
  "https://www.dev.meetusvr.com/files/shop360/pannellum/v3/index.html";

const get360Url = ({ shopId, productId }) => {
  const shop = shopId ? `&shop_id=${shopId}` : "";
  const product = productId ? `&product_id=${productId}` : "";
  return `${full360Url}?autoLoad=true${shop}${product}`;
};

const defaultChatSettings = (orgData) => {
  return {
    mode: "light",
    color: orgData.theme?.settings?.colors?.primary || "#000",
    title: `Explore ${orgData?.name}`,
    welcoming: "Hi, what can I help you with?",
    images: [],
  };
};

function hexToRgb(hex, alpha) {
  hex = hex.replace("#", "");
  const r = parseInt(
    hex.length === 3 ? hex.slice(0, 1).repeat(2) : hex.slice(0, 2),
    16
  );
  const g = parseInt(
    hex.length === 3 ? hex.slice(1, 2).repeat(2) : hex.slice(2, 4),
    16
  );
  const b = parseInt(
    hex.length === 3 ? hex.slice(2, 3).repeat(2) : hex.slice(4, 6),
    16
  );
  if (alpha) {
    return "rgba(" + r + ", " + g + ", " + b + ", " + alpha + ")";
  } else {
    return r + ", " + g + ", " + b;
  }
}

// Relative luminance (WCAG) of a brand color. Handles 3/6/8-digit hex (alpha ignored).
function brandLuminance(hex) {
  const [r, g, b] = hexToRgb(hex).split(",").map((n) => parseInt(n, 10));
  const toLin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
}

// WCAG contrast ratio between two luminances.
function contrastRatio(l1, l2) {
  const [light, dark] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (light + 0.05) / (dark + 0.05);
}

// Pick whichever text color (dark or white) has the higher contrast ratio
// against the brand background — works correctly for all hues and mid-tones.
function getContrastText(hex) {
  const bgLum = brandLuminance(hex);
  const darkText = "#1a1a1a";
  const darkLum = brandLuminance(darkText);
  return contrastRatio(bgLum, darkLum) >= contrastRatio(bgLum, 1)
    ? darkText
    : "#ffffff";
}

// How long the widget waits for the owner's sign-in sentence before giving up
// on it for this load. The three reads it travels with decide whether the
// widget appears at all, so this one is never allowed to be the slowest thing
// on the page: past this, the panel uses its built-in wording instead.
export const SIGN_IN_OFFER_TIMEOUT_MS = 2000;

/**
 * The brand owner's sign-in sentence, read once per widget load alongside the
 * settings it belongs with.
 *
 * It is contained twice, because the batch below rejects as a whole and the
 * http client sets no timeout of its own. Any rejection becomes an empty line,
 * and a server that never answers is given SIGN_IN_OFFER_TIMEOUT_MS and no
 * longer. Either way this resolves, with an empty line, and the widget keeps
 * its built-in wording - a brand with no sentence is nearly every brand, and
 * none of them may lose their panel over a sentence they never wrote.
 */
const readGuestSignInLine = (orgId) =>
  new Promise((resolve) => {
    const asLine = (line) => (typeof line === "string" ? line.trim() : "");
    const giveUp = setTimeout(() => resolve(""), SIGN_IN_OFFER_TIMEOUT_MS);
    const settle = (line) => {
      clearTimeout(giveUp);
      resolve(asLine(line));
    };
    try {
      Services.getGuestSignInOffer({ orgId })
        .then((response) => settle(response?.data?.line))
        .catch(() => settle(""));
    } catch (e) {
      settle("");
    }
  });

export const getDataHandling = async ({ orgId, preview, dashboardToken }) => {
  const [
    { data: orgData },
    { data: settingsData },
    { data: shopsData },
    guestSignInLine,
  ] = await Promise.all([
      Services.getOrganization({ orgId: orgId }),
      Services.getChatSettings({
        orgId: orgId,
        type: preview ? "get-unpublished" : "get-published",
        dashboardToken,
      }),
      Services.getShops(orgId),
      readGuestSignInLine(orgId),
    ]);

  let chatSettingsData = JSON.parse(settingsData?.value);

  if (!chatSettingsData) {
    chatSettingsData = defaultChatSettings(orgData);
  }

  const shopHas360 = shopsData?.content?.find((item) => {
    if (item?.has_360) return item;
    else return null;
  });

  return { orgData, chatSettingsData, shopsData, shopHas360, guestSignInLine };
};

export const handleChatImages = ({ chatSettingsData, orgData }) => {
  chatSettingsData?.images?.forEach((img) => {
    if (orgImageTypes[img?.type]) {
      if (orgImageTypes[img?.type] === orgImageTypes["332"]) {
        chatSettingsData.images[orgImageTypes[img?.type]] = [
          ...(chatSettingsData.images[orgImageTypes[img?.type]] || []),
          img?.image_url,
        ];
      } else {
        chatSettingsData.images[orgImageTypes[img?.type]] = img?.image_url;
      }
    }
  });

  if (!chatSettingsData?.images) {
    chatSettingsData.images = [];
  }

  /**
   * Convert orgData.images array into a dictionary of filtered image types.
   *
   * @param {Object} orgData - The organization data object.
   * @returns {Object} orgImagesDict - A dictionary of image URLs, where the keys are the image types.
   */
  const orgImagesDict = orgData?.images
    ?.filter((im) => orgImageTypes.hasOwnProperty(im?.type))
    ?.reduce((dict, im) => {
      return { ...dict, [im.type]: im.url };
    }, {});

  chatSettingsData.images["logo_url"] =
    chatSettingsData.images["logo_url"] || orgImagesDict?.[1];

  chatSettingsData.images["chat_services_widget_background"] =
    chatSettingsData.images["chat_services_widget_background"] ||
    orgImagesDict?.[301];

  return chatSettingsData;
};

// Read once and kept. These describe the frame the widget was created in, and
// they do not change while it lives — but the app's own navigation strips the
// query string, so re-reading the URL on every call lost orgId, the origin the
// message check relies on, and every embed flag, the moment a route changed.
let embedParams = null;

export const getOriginUrlAndOrgId = () => {
  if (embedParams) return embedParams;
  const urlParams = new URLSearchParams(window.location.search);
  // Read through the leaf the store uses, so "which shop is this" has one
  // answer. The store is built before this module is reachable and cannot
  // import from here.
  const orgId = getEmbeddedOrgId();
  const preview = urlParams.get("preview");
  const originUrl = urlParams.get("url") || "*";
  const dashboardToken = urlParams.get("dashboardToken");
  // "lean" means the widget is embedded on a brand's shop rather than standing
  // on its own. A customer there came to buy a cake, not to visit our app, so
  // the panel drops everything that is not the conversation.
  const chrome = urlParams.get("chrome");
  const parsed = {
    orgId,
    originUrl,
    preview: preview === "true",
    dashboardToken,
    leanChrome: chrome === "lean",
  };
  // Only remember a real embed. A bare load with no orgId is the standalone
  // app finding its feet, and must not pin an empty answer for good.
  if (orgId) embedParams = parsed;
  return parsed;
};

// What goes out of this frame to the page hosting it.
//
// Addressed to the shop that embedded us, and to nobody else. It used to fall
// back to "*" — any page anywhere — whenever the host address was missing or
// malformed. That was already broad for a finished answer; it now also carries
// the words the customer typed, which is theirs and not ours to broadcast.
//
// An unknown host means the message is not sent. A layout hint or a mirrored
// question not arriving is a surface that does not update; the alternative is
// a customer's question handed to whatever is listening.
export const postMessage = (message) => {
  const { originUrl } = getOriginUrlAndOrgId();
  if (!originUrl || originUrl === "*") return;

  let targetOrigin;
  try {
    targetOrigin = new URL(originUrl).origin;
  } catch (e) {
    return;
  }
  securePostMessage(message, targetOrigin);
};

export const isArabicText = (text) => /[\u0600-\u06FF]/.test(text);

export const getImageByType = (imageList, type) =>
  imageList?.find((image) => image?.type === type)?.image_url;

/**
 * Separates messages into customer and agent messages
 * @param {Array} messages - Array of message objects
 * @returns {Object} Object containing separated messages and counts
 */
export const separateMessages = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) {
    return {
      customerMessages: [],
      agentMessages: [],
      botMessages: [],
      totalCount: 0,
      customerCount: 0,
      agentCount: 0,
      botCount: 0,
    };
  }

  const customerMessages = [];
  const agentMessages = [];
  const botMessages = [];

  messages.forEach((message) => {
    if (!message || typeof message !== 'object') return;

    // Validate and sanitize message content
    if (message.content && typeof message.content === 'string') {
      message.content = validateInput(message.content, { maxLength: 5000 });
    }

    const senderType =
      message.senderType || (message.sender?.agent ? "AGENT" : "USER") || "BOT";

    switch (senderType) {
      case "AGENT":
        agentMessages.push(message);
        break;
      case "USER":
        customerMessages.push(message);
        break;
      case "BOT":
        botMessages.push(message);
        break;
      default:
        // Default to customer if type is unknown
        customerMessages.push(message);
    }
  });

  return {
    customerMessages,
    agentMessages,
    botMessages,
    totalCount: messages.length,
    customerCount: customerMessages.length,
    agentCount: agentMessages.length,
    botCount: botMessages.length,
  };
};

const imageExtensions = [".png", ".jpg", ".svg"];
const modelExtensions = [".gltf", ".glb"];

export const imageUrls = (urls) =>
  urls?.filter((url) =>
    imageExtensions.some((extension) => url.endsWith(extension))
  );

export const modelUrls = (urls) =>
  urls?.filter((url) =>
    modelExtensions.some((extension) => url.endsWith(extension))
  );

const Utils = {
  set360Cookies(userToken) {
    const cookieData = {
      backend_api_url: baseUrl,
      files_api_url: isDev ? `${baseUrl}/files` : "/files",
    };
    if (userToken) {
      cookieData.user_token = userToken;
    }
    document.cookie = `360=${JSON.stringify(cookieData)}; path=/; domain=${
      window.location.host
    };`;
  },
  getProper360Url(shopId) {
    return get360Url({ shopId });
  },
  getProperProduct360Url(shopId, productId) {
    return get360Url({ shopId, productId });
  },
  getProperImageUrl(imagePath) {
    // Already an address: absolute, or protocol-relative. The product list the
    // server sends with an answer may give a picture as a full address, and
    // putting the files path in front of one makes a link to nothing.
    if (typeof imagePath === "string" && /^(https?:)?\/\//.test(imagePath)) return imagePath;
    return `${baseUrl}/files/${imagePath}`;
  },
  getProductPrices(product) {
    const { price, discount } = product;
    const sale = Number(price - price * ((discount || 0) / 100));
    return { sale, original: price };
  },
  getProductPricesDetails(product) {
    const { price, discount } = product;
    const sale = Number(price - discount);
    return { sale, original: price };
  },
  calculateDiscountPercentage(price, discount) {
    return (discount / price) * 100;
  },
  isNewProduct(creationDate) {
    return new Date().getTime() === new Date(creationDate).getTime();
  },
  setAppColors(themeSettings) {
    const { colors = {} } = themeSettings;
    Object.entries(colors || {}).forEach(([color, value]) => {
      document.documentElement.style.setProperty(
        "--" + color.replace("_", "-") + "-color", // eg: --primary-color
        value
      );
      document.documentElement.style.setProperty(
        "--" + color.replace("_", "-") + "-color-rgb", // eg: --primary-color-rgb
        hexToRgb(value)
      );
    });
  },
  setWidgetColors(color) {
    const safeColor =
      typeof color === "string" &&
      /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(color)
        ? color
        : "#019AA6";

    document.documentElement.style.setProperty("--primary-color", safeColor);
    document.documentElement.style.setProperty(
      "--primary-color-rgb",
      hexToRgb(safeColor)
    );
    // Also set chat-widget-bg for SideServicesApp (floating widget)
    document.documentElement.style.setProperty("--chat-widget-bg", safeColor);
    // Readable text color on brand-colored surfaces (fixes white-on-white when brand is light)
    document.documentElement.style.setProperty(
      "--primary-text",
      getContrastText(safeColor)
    );
    // Subtle border so a near-white brand button keeps an edge against a light bg
    document.documentElement.style.setProperty(
      "--primary-border",
      brandLuminance(safeColor) > 0.85 ? "rgba(0, 0, 0, 0.12)" : "transparent"
    );
  },
};

export default Utils;
